import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { TbPlus, TbTrash, TbInfoCircle } from 'react-icons/tb';
import SearchDropdown from '../../../../../../../../../../../../../../../../components/common/SearchDropdown/SearchDropdown';
import { getAllIngredientsByLocalId } from '../../../../../../../../../../../../../../../../crud/menu_api/ingredients';
import { getAllUnidadesMedida,getAllUnitConversions } from '../../../../../../../../../../../../../../../../crud/configuration_api/unidades_medida';
import './IngredientsManager.scss';

export interface Option { id: number; name: string; unidad_medida_id?: number }
export interface IngredientSelection {
  id: number | string;
  name: string;
  units: number | null;
  unidad_medida_id: number;
}

export interface UnitConversion {
  from_unidad_medida_id: number;
  to_unidad_medida_id: number;
  factor: number;
  from_name: string;
  from_acronym: string;
  to_name: string;
  to_acronym: string;
}

interface IngredientsManagerProps {
  ingredientSelections: IngredientSelection[];
  setIngredientSelections: React.Dispatch<React.SetStateAction<IngredientSelection[]>>;
}

const IngredientsManager: React.FC<IngredientsManagerProps> = ({
  ingredientSelections,
  setIngredientSelections,
}) => {
  const [ingredientOptions, setIngredientOptions] = useState<Option[]>([]);
  const [unidadMedidaOptions, setUnidadMedidaOptions] = useState<Option[]>([]);
  const [unitConversions, setUnitConversions] = useState<UnitConversion[]>([]);
  const [loading, setLoading] = useState(false);

  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const localId = user?.local_id;

  // Function to get available units for a specific ingredient
  const getAvailableUnitsForIngredient = (ingredientId: number | string): Option[] => {
    if (!ingredientId) return unidadMedidaOptions;
    
    // Find the selected ingredient and its base unit
    const selectedIngredient = ingredientOptions.find(ing => ing.id === ingredientId);
    if (!selectedIngredient || !selectedIngredient.unidad_medida_id) {
      return unidadMedidaOptions;
    }
    
    const baseUnitId = selectedIngredient.unidad_medida_id;
    
    // Always include the base unit
    const availableUnits = unidadMedidaOptions.filter(unit => unit.id === baseUnitId);
    
    // Find convertible units from the base unit
    const convertibleUnits = unitConversions
      .filter(conv => conv.from_unidad_medida_id === baseUnitId)
      .map(conv => conv.to_unidad_medida_id);
    
    // Find convertible units to the base unit (reverse conversions)
    const reverseConvertibleUnits = unitConversions
      .filter(conv => conv.to_unidad_medida_id === baseUnitId)
      .map(conv => conv.from_unidad_medida_id);
    
    // Combine all convertible unit IDs
    const allConvertibleUnitIds = [...convertibleUnits, ...reverseConvertibleUnits];
    
    // Add convertible units to available units
    const additionalUnits = unidadMedidaOptions.filter(unit => 
      allConvertibleUnitIds.includes(unit.id) && !availableUnits.some(au => au.id === unit.id)
    );
    
    return [...availableUnits, ...additionalUnits];
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([getAllUnidadesMedida(), getAllIngredientsByLocalId(localId), getAllUnitConversions()])
      .then(([umResp, ingResp, convResp]: any[]) => {
        if (!mounted) return;
        if (umResp) {
          const ums: Option[] = umResp.data.map((u: any) => ({ id: u.unidad_medida_id, name: `${u.name} - ${u.acronym || ''}`.trim() }));
          setUnidadMedidaOptions(ums);
        }
        if (ingResp) {
          const ings: Option[] = ingResp.data.map((ing: any) => ({ 
            id: ing.ingredient_id, 
            name: ing.ingredient_name,
            unidad_medida_id: ing.unidad_medida_id // Include base unit info
          }));
          // Sort by name
          ings.sort((a, b) => a.name.localeCompare(b.name));
          setIngredientOptions(ings);
        }
        if (convResp) {
          setUnitConversions(convResp.data || []);
        }
      })
      .catch((err) => console.error('Error loading ingredient data', err))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [localId]);

  const handleIngredientChange = (index: number, field: keyof IngredientSelection, value: any) => {
    const updated = [...ingredientSelections];
    updated[index] = { ...updated[index], [field]: value } as IngredientSelection;
    
    if (field === 'id') {
      const sel = ingredientOptions.find((opt) => opt.id === value);
      if (sel) {
        updated[index].name = sel.name;
        // Reset to the base unit of the ingredient when ingredient changes
        if (sel.unidad_medida_id) {
          updated[index].unidad_medida_id = sel.unidad_medida_id;
        }
      }
    }
    setIngredientSelections(updated);
  };

  const handleAddRow = () => {
    const defaultUm = unidadMedidaOptions[0]?.id || 1;
    setIngredientSelections([
      ...ingredientSelections,
      { id: '', name: '', units: 1, unidad_medida_id: defaultUm },
    ]);
  };

  const handleRemove = (idx: number) => setIngredientSelections(ingredientSelections.filter((_, i) => i !== idx));

  return (
    <div className="ingredients-manager">
      <div className="config-section">
        <div className="section-header">
          <div className="section-title-group">
            <h6 className="section-title">Ingredientes</h6>
            <span className="ingredient-count">
              {ingredientSelections.length} ingrediente{ingredientSelections.length !== 1 ? 's' : ''}
            </span>
          </div>
          <Button variant="none" className="add-ingredient-btn" onClick={handleAddRow} disabled={loading}>
            <TbPlus className="plus-icon" /> Agregar ingrediente
          </Button>
        </div>

        <div className="ingredient-rows">
          {loading && (
            <div className="loading-state">
              <div className="loading-content">
                <Spinner animation="border" size="sm" className="loading-spinner" />
                <span className="loading-text">Cargando ingredientes...</span>
              </div>
            </div>
          )}

          {!loading && ingredientSelections.length === 0 && (
            <div className="empty-state" onClick={handleAddRow}>
              <TbPlus /> Agrega tu primer ingrediente
            </div>
          )}

          {ingredientSelections.map((sel, idx) => (
            <div key={idx} className="ingredient-row">
              <div className="col qty">
                <Form.Label className="sr-only">Cantidad</Form.Label>
                <Form.Control
                  type="number"
                  className="modern-input compact quantity-input"
                  value={sel.units ?? ''}
                  min={0}
                  step={0.01}
                  onChange={(e) => handleIngredientChange(idx, 'units', e.target.value === '' ? null : Number(e.target.value))}
                  placeholder="Cant."
                />
              </div>

              <div className="col um">
                {sel.id && getAvailableUnitsForIngredient(sel.id).length < unidadMedidaOptions.length && (
                  <div className="unit-restriction-badge">
                    <TbInfoCircle /> Unidades limitadas
                  </div>
                )}
                <SearchDropdown
                  items={getAvailableUnitsForIngredient(sel.id)}
                  selected={getAvailableUnitsForIngredient(sel.id).find((opt) => opt.id === sel.unidad_medida_id) || null}
                  setSelected={(item: Option) => handleIngredientChange(idx, 'unidad_medida_id', item.id)}
                  displayProperty="name"
                  idProperty="id"
                  placeholder={getAvailableUnitsForIngredient(sel.id).length === 0 ? 'Sin unidades' : 'Unidad'}
                  disabled={getAvailableUnitsForIngredient(sel.id).length === 0 || !sel.id}
                />
              </div>

              <div className="col ing">
                <SearchDropdown
                  items={ingredientOptions}
                  selected={ingredientOptions.find((opt) => opt.id === sel.id) || null}
                  setSelected={(item: Option) => handleIngredientChange(idx, 'id', item.id)}
                  displayProperty="name"
                  idProperty="id"
                  placeholder={ingredientOptions.length === 0 ? 'Cargando...' : 'Buscar ingrediente'}
                  disabled={ingredientOptions.length === 0}
                />
              </div>

              <div className="col actions">
                <Button variant="none" className="action-button delete" onClick={() => handleRemove(idx)} aria-label="Eliminar">
                  <TbTrash />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IngredientsManager;
